import React, { useEffect, useState } from 'react';

/**
 * A lightweight, self-contained "center toast" — the friendlier alternative
 * to sonner's bottom notifications for everyday confirmations (copy link,
 * "coming soon" taps, attendance, etc). It appears as a small rounded pill
 * with a cute contextual emoji icon, dead-centre of the screen, and fades
 * out on its own. Validation *errors* intentionally keep using sonner's
 * `toast.error` (top-center) — this is only for the friendlier confirmations.
 *
 * Usage: centerToast('Link copied!', '🔗');
 */
let dispatch = null;
let uid = 0;

export function centerToast(message, icon = '✨') {
  if (dispatch) dispatch({ id: ++uid, message, icon });
}

function CenterToastItem({ message, icon, onDone }) {
  const [shown, setShown] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const holdTimer = setTimeout(() => setLeaving(true), 1700);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(holdTimer);
    };
  }, []);

  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(onDone, 220);
    return () => clearTimeout(t);
  }, [leaving, onDone]);

  const visible = shown && !leaving;

  return (
    <div
      className={`pointer-events-auto flex items-center gap-2.5 max-w-[84vw] rounded-full border border-border/60 bg-white/95 backdrop-blur-sm shadow-xl pl-2.5 pr-5 py-2.5 transition-all duration-200 ease-out ${
        visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-1 scale-90 opacity-0'
      }`}
    >
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-base leading-none">
        {icon}
      </span>
      <span className="text-sm font-semibold leading-snug text-[#0F172A]">{message}</span>
    </div>
  );
}

export default function CenterToastHost() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    dispatch = (toast) => setToasts((prev) => [...prev, toast]);
    return () => {
      dispatch = null;
    };
  }, []);

  const remove = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-6 pointer-events-none">
      <div className="flex flex-col items-center gap-2">
        {toasts.map((t) => (
          <CenterToastItem key={t.id} message={t.message} icon={t.icon} onDone={() => remove(t.id)} />
        ))}
      </div>
    </div>
  );
}
